import React from "react";
import { motion } from "framer-motion";
import { SOFTWARE_SERVICES, TRAINING_TRACKS, TRAINING_PROMISES } from "@/lib/site";
import { Check } from "lucide-react";

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="surface-band py-12 sm:py-16 md:py-24"
    >
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        {/* Centered Section Header */}
        <div className="flex flex-col items-center text-center">
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-heading max-w-[800px] text-[#222222]"
          >
            What we build. What we teach.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-4 sm:mt-6 text-body-editorial max-w-[640px] text-[#222222]"
          >
            We operate across two intertwined domains: full-lifecycle software
            engineering for forward-thinking businesses, and intensive apprenticeship
            tracks that prepare engineers for high-impact roles.
          </motion.p>
        </div>

        {/* Subsection 1: Software Development */}
        <div id="software-development" data-testid="software-development" className="mt-12 sm:mt-16 md:mt-20">
          <div className="text-center max-w-[640px] mx-auto mb-6 sm:mb-10">
            <h3 className="text-heading-sm text-[#222222]">
              Software Development
            </h3>
            <p className="mt-1.5 sm:mt-2 text-body-sm-editorial text-[#6a6a6a]">
              Production-grade builds delivered by experienced engineers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {SOFTWARE_SERVICES.map((s, i) => (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`software-card-${i + 1}`}
                className="bg-white rounded-[12px] border border-[#ebebeb] p-5 sm:p-6 md:p-8 flex flex-col justify-between"
              >
                <div>
                  <span className="text-[12px] sm:text-[13px] font-medium text-[#6a6a6a] uppercase tracking-wider">
                    {s.tag}
                  </span>
                  <h4 className="mt-3 sm:mt-4 text-subheading text-[#222222]">
                    {s.title}
                  </h4>
                  <p className="mt-2.5 sm:mt-4 text-body-sm-editorial text-[#6a6a6a]">
                    {s.body}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>

        {/* Media Block in rhythm */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-10 sm:mt-16"
        >
          <figure className="media-frame relative aspect-[16/10] sm:aspect-[16/9] md:aspect-[21/9] w-full">
            <img
              src="https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?auto=format&fit=crop&w=2400&q=80"
              alt="Engineering team collaborating on live project"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </figure>
        </motion.div>

        {/* Subsection 2: Industry Training */}
        <div id="industry-training" data-testid="industry-training" className="mt-14 sm:mt-20">
          <div className="text-center max-w-[640px] mx-auto mb-6 sm:mb-10">
            <h3 className="text-heading-sm text-[#222222]">
              Industry Training &amp; Apprenticeship
            </h3>
            <p className="mt-1.5 sm:mt-2 text-body-sm-editorial text-[#6a6a6a]">
              A six-month, hands-on programme built inside an active studio.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {/* Tracks List */}
            <div className="bg-white rounded-[12px] border border-[#ebebeb] p-5 sm:p-6 md:p-8">
              <h4 className="text-[17px] sm:text-[18px] font-medium text-[#222222] mb-4 sm:mb-6">
                Specialized Tracks
              </h4>
              <ul className="divide-y divide-[#ebebeb]">
                {TRAINING_TRACKS.map((t) => (
                  <li
                    key={t.code}
                    data-testid={`training-track-${t.code}`}
                    className="py-3 sm:py-3.5 flex items-center justify-between gap-3"
                  >
                    <div>
                      <p className="text-[15px] sm:text-[16px] font-medium text-[#222222]">{t.title}</p>
                      <p className="text-[12px] sm:text-[13px] text-[#6a6a6a]">{t.note}</p>
                    </div>
                    <span className="text-[11px] sm:text-[12px] font-medium text-[#6a6a6a] bg-[#f7f7f7] px-2 py-0.5 sm:py-1 rounded-[4px]">
                      {t.code}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Promises List */}
            <div className="bg-white rounded-[12px] border border-[#ebebeb] p-5 sm:p-6 md:p-8 flex flex-col justify-between">
              <div>
                <h4 className="text-[17px] sm:text-[18px] font-medium text-[#222222] mb-4 sm:mb-6">
                  The Studio Guarantee
                </h4>
                <div className="space-y-4 sm:space-y-5">
                  {TRAINING_PROMISES.map((p) => (
                    <div key={p.k} className="flex items-start gap-3">
                      <div className="mt-0.5 h-5 w-5 rounded-[4px] bg-[#f7f7f7] border border-[#ebebeb] flex items-center justify-center shrink-0">
                        <Check size={13} className="text-[#222222]" />
                      </div>
                      <div>
                        <p className="text-[14px] sm:text-[15px] font-medium text-[#222222]">{p.k}</p>
                        <p className="text-[13px] sm:text-[14px] text-[#6a6a6a]">{p.v}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-[#ebebeb]">
                <a
                  href="#contact"
                  className="btn-primary-action w-full text-center"
                >
                  Apply for next cohort
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Map Visualization Container per DESIGN.md */}
        <div className="mt-14 sm:mt-20">
          <div className="rounded-[12px] bg-[#ebebeb] p-5 sm:p-8 md:p-12 text-center">
            <h4 className="text-[18px] sm:text-[20px] font-medium text-[#222222]">
              Pune Studio &amp; Global Placement Network
            </h4>
            <p className="mt-2 text-[14px] sm:text-[15px] text-[#6a6a6a] max-w-[580px] mx-auto">
              Based out of Pune's technology corridor, delivering software and placing engineers with hiring partners worldwide.
            </p>

            <div className="mt-6 sm:mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-[800px] mx-auto">
              <div className="bg-white rounded-[8px] p-3 sm:p-4 text-center border border-[#ebebeb]">
                <p className="text-[12px] sm:text-[13px] text-[#6a6a6a]">Studio Hub</p>
                <p className="text-[14px] sm:text-[16px] font-medium text-[#222222] mt-0.5 sm:mt-1">Pune, India</p>
              </div>
              <div className="bg-white rounded-[8px] p-3 sm:p-4 text-center border border-[#ebebeb]">
                <p className="text-[12px] sm:text-[13px] text-[#6a6a6a]">Delivery Focus</p>
                <p className="text-[14px] sm:text-[16px] font-medium text-[#222222] mt-0.5 sm:mt-1">Global SaaS</p>
              </div>
              <div className="bg-white rounded-[8px] p-3 sm:p-4 text-center border border-[#ebebeb]">
                <p className="text-[12px] sm:text-[13px] text-[#6a6a6a]">Training Model</p>
                <p className="text-[14px] sm:text-[16px] font-medium text-[#222222] mt-0.5 sm:mt-1">Live Projects</p>
              </div>
              <div className="bg-white rounded-[8px] p-3 sm:p-4 text-center border border-[#ebebeb]">
                <p className="text-[12px] sm:text-[13px] text-[#6a6a6a]">Placement Rate</p>
                <p className="text-[14px] sm:text-[16px] font-medium text-[#222222] mt-0.5 sm:mt-1">94% Verified</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
